import { cx } from 'class-variance-authority'
import React from 'react'
import type { DropzoneOptions } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'

import { CheckCircle, CloudUpload } from '../icons'
import { classes } from '../utils'

export type FileUploaderProps = {
  prompt?: string | React.ReactElement
  subPrompt?: string | React.ReactElement
  icon?: 'cloud' | 'fileAdded' | React.ReactElement
  className?: string
} & Pick<Required<DropzoneOptions>, 'onDrop'> &
  Omit<DropzoneOptions, 'onDrop'>

export default function FileUploader({
  prompt,
  subPrompt,
  icon = 'cloud',
  onDrop,
  disabled,
  className,
  ...dropOptions
}: FileUploaderProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    ...dropOptions,
  })

  const renderPrompt = () => {
    if (isDragActive) {
      return (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-300">
          Drop your file here!
        </p>
      )
    } else if (typeof prompt === 'string') {
      return (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-300">
          {prompt}
        </p>
      )
    } else if (!!prompt) {
      return prompt
    }

    return (
      <p className="mb-2 text-foreground-subtle dark:text-foreground-on-inverse">
        <span className="font-semibold">Click to upload</span> or drag and drop
      </p>
    )
  }

  const renderSubPrompt = () => {
    if (typeof subPrompt === 'string') {
      return (
        <p className="text-xs text-gray-500 dark:text-gray-300">{subPrompt}</p>
      )
    } else if (!!subPrompt) {
      return subPrompt
    }

    return null
  }

  const renderIcon = () => {
    if (!!icon && typeof icon !== 'string') {
      return icon
    } else if (icon === 'cloud') {
      return (
        <CloudUpload className="mb-3 h-10 w-10 text-foreground-subtle dark:text-foreground-on-inverse" />
      )
    } else if (icon === 'fileAdded') {
      return (
        <CheckCircle className="mb-3 h-10 w-10 text-foreground-subtle dark:text-foreground-on-inverse" />
      )
    }

    return null
  }

  return (
    <div
      {...getRootProps()}
      className={classes(
        'relative flex h-full w-full items-center justify-center',
        className,
      )}
      data-testid="dragZone"
    >
      <label
        htmlFor="file-upload"
        className={cx(
          disabled
            ? 'cursor-not-allowed'
            : 'cursor-pointer hover:bg-gray-100 dark:hover:border-gray-500 dark:hover:bg-gray-800',
          'relative flex h-64 w-full flex-col items-center justify-center rounded-lg border border-border-normal drop-shadow-sm transition-colors duration-100 dark:border-gray-600',
        )}
        aria-disabled={disabled}
      >
        <div className="relative flex flex-col items-center justify-center pb-6 pt-5">
          {renderIcon()}
          {renderPrompt()}
          {renderSubPrompt()}
        </div>
        <input id="file-upload" {...getInputProps()} className="hidden" />
      </label>
    </div>
  )
}
